// generateProductJsonLd.js
import { getProductObject } from '../resource/SchemaObject.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import { getLanguageTranslationKey } from '../resource/getCurrentLanguage.js';
import { DEFAULT_LANGUAGE, localeConvertor } from '../resource/i18nCommon.js';

/**
 * Generate Product JSON-LD with multilingual support
 * @param {Object} options
 * @param {string} options.productId - Product ID
 * @param {string} options.productType - Product type
 * @param {string} options.title - Product title (for meta tags)
 * @param {string} options.description - Product description (for meta tags)
 * @param {string} options.releaseDate - Release date (ISO 8601)
 * @param {Array} options.offers - Array of price information
 * @param {Array} options.interactionStatistic - Array of interaction statistics
 * @param {string} [options.origin='https://swag.live'] - Website origin
 * @param {Object} options.i18n - i18n instance (optional, for multilingual support)
 * @param {number} options.price - Product price (optional, for multilingual description)
 * @param {string} options.currency - Currency code (optional, for multilingual description)
 * @param {number} options.decimalFactor - Decimal factor (optional, for multilingual description)
 * @param {Array} options.secondaryBadge - Secondary badge data (optional, for multilingual description)
 * @param {Array} options.availableLanguages - Available languages (optional, for multilingual JSON-LD)
 * @returns {Object} Product JSON-LD
 */
export const generateProductJsonLd = async ({
  productId,
  productType = 'Digital Product',
  title,
  description,
  releaseDate,
  offers = [],
  interactionStatistic = [],
  origin = 'https://swag.live',
  i18n,
  price,
  currency,
  decimalFactor,
  secondaryBadge = [],
  availableLanguages = [],
}) => {
  // If no i18n or no availableLanguages, return simple version
  if (!i18n || !availableLanguages.length) {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        getProductObject({
          id: productId,
          type: productType,
          title,
          description,
          nbf: releaseDate,
          offers,
          interactionStatistic,
          origin,
        }),
      ],
    };
  }

  // Prepare multilingual data (same as Next.js)
  const alternateLanguages = availableLanguages.filter(
    lang => DEFAULT_LANGUAGE !== lang
  );

  // Load all language resources for translation
  const loadLanguagePromises = alternateLanguages.map(async lang => {
    if (!i18n.hasResourceBundle(lang, TranslationNamespace.SEO)) {
      await i18n.reloadResources(lang, TranslationNamespace.SEO);
    }
  });
  await Promise.all(loadLanguagePromises);

  // Get title tail for SEO
  const titleTail = i18n.t('seo_global_title_tail', {
    ns: TranslationNamespace.SEO,
  });

  // Build title translation function
  const getTitleByLang = lang =>
    i18n.t(getTranslationKey({ key: `shop_product_name_${productId}` }), {
      ns: TranslationNamespace.PRODUCT,
      lng: lang,
    });

  // Build multilingual title
  const name = {
    '@value': getTitleByLang(DEFAULT_LANGUAGE),
    '@language': localeConvertor({ locale: DEFAULT_LANGUAGE, isBCP47: true }),
  };

  // Build description translation function
  const [discountKey, discountValue] = secondaryBadge;
  const getPriceDescription = lang => {
    const langOption = lang ? { lng: lang } : {};
    const purchaseTextLang = i18n.t('purchase_message_pack', {
      ns: TranslationNamespace.SHOP,
      ...langOption,
    });

    const discountTextLang = secondaryBadge.length
      ? i18n.t(
          getTranslationKey({
            key: `shop_product_badge_${discountKey}`,
          }),
          {
            discount: discountValue,
            ns: TranslationNamespace.PRODUCT,
            ...langOption,
          }
        )
      : '';

    return [
      purchaseTextLang,
      `${price / decimalFactor} ${currency}`,
      discountTextLang,
    ]
      .filter(i => i)
      .join(' ');
  };

  // Build multilingual description
  const multilingualDescription = {
    '@value': getPriceDescription(DEFAULT_LANGUAGE),
    '@language': localeConvertor({ locale: DEFAULT_LANGUAGE, isBCP47: true }),
  };

  // Helper functions for multilingual support
  const localeToBCP47 = lang =>
    localeConvertor({ locale: lang, isBCP47: true });

  const buildAlternateValues = translateFn =>
    alternateLanguages.map(lang => ({
      '@value': translateFn(lang),
      '@language': localeToBCP47(lang),
    }));

  // Build inLanguage array
  const inLanguage = availableLanguages.map(lang => ({
    '@type': 'Language',
    name: i18n.t(getLanguageTranslationKey({ language: lang }), {
      ns: TranslationNamespace.SEO,
    }),
    alternateName: localeToBCP47(lang),
  }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      getProductObject({
        id: productId,
        type: productType,
        title: getTitleByLang(DEFAULT_LANGUAGE),
        titleTail,
        name,
        description: multilingualDescription,
        nbf: releaseDate,
        offers,
        interactionStatistic,
        inLanguage,
        alternateName: buildAlternateValues(getTitleByLang),
        alternateDescription: buildAlternateValues(getPriceDescription),
        origin,
      }),
    ],
  };
};
